import React, { useEffect, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import type { NextPage } from 'next';

import { Box, Divider, Typography, useMediaQuery } from '@mui/material';

import { DateTime } from 'luxon';
import { useRecoilValue } from 'recoil';

import { useAnnouncements } from '@hooks_iqfu/dashboard/useAnnouncements';
import { useDashboardData } from '@hooks_iqfu/dashboard/useDashboardData';

import DashboardInfoCard from '@components_iqfu/dashboard/dashboard_info_card/DashboardInfoCard';
import DashboardSaleCases from '@components_iqfu/dashboard/dashboard_sale_cases/DashboardSaleCases';

import { companyLoginUserDataAtom, companyLoginUserDataSelector } from '@recoil_iqfu/atoms/auth/companyLoginUserData';

import { AnnouncementsType } from '@type-def_iqfu/AnnouncementsType';
import { DashboardCompanyCountType } from '@type-def_iqfu/DashboardType';

import DashboardPageContainer from '@components_common/page_container/DashboardPageContainer';
import { MediaQuery } from '@constants_common';
import { DateOutputFormatWithColon } from '@constants_iqfu';
import styles from '@styles_iqfu/dashboard/dashboard.module.scss';

const Dashboard: NextPage = () => {
  const router = useRouter();
  const { getCompanyDashboardData } = useDashboardData();
  const { getAnnouncementsData } = useAnnouncements();

  const companyLoginUserData = useRecoilValue(companyLoginUserDataAtom);
  const { companyId, isMultipleCompanyUser } = useRecoilValue(companyLoginUserDataSelector);

  const [dashboardCount, setDashboardCount] = useState<DashboardCompanyCountType>();
  const [announcements, setAnnouncements] = useState<AnnouncementsType[]>([]);
  const isMobile = useMediaQuery(MediaQuery.sm);
  const [hasUnread, setHasUnread] = useState(false);

  const serviceList = companyLoginUserData?.services;

  const iqfuAccessibleAuth = () => {
    return serviceList?.includes('iqfu');
  };

  const proAccessibleAuth = () => {
    return serviceList?.includes('pro');
  };

  useEffect(() => {
    if (iqfuAccessibleAuth()) {
      fetchCompanies();
      fetchAnnouncements();
    } else if (proAccessibleAuth()) {
      router.replace(`/console/company/iqfu_pro`);
    }
  }, []);

  const fetchCompanies = async () => {
    const result = await getCompanyDashboardData();
    setDashboardCount(result as DashboardCompanyCountType);
    setHasUnread(result.unread_selling_room_count > 0);
  };

  const fetchAnnouncements = async () => {
    const result = await getAnnouncementsData('company', {
      offset: 0,
      limit: 4,
      latest: true,
      destination: 'company',
    });
    setAnnouncements(result.announcements);
  };

  return (
    <DashboardPageContainer userType="company" isCompanyMobile={isMobile}>
      {iqfuAccessibleAuth() && (
        <>
          {dashboardCount && dashboardCount.rejected_sale_case_count > 0 && !isMobile && (
            <div className={styles.info_wrap}>
              <div className={styles.company_info_title}>
                <h4 className={styles.title_forward}>差し戻し売却実績</h4>
                <h4 className={styles.company_request_counter}>{dashboardCount.rejected_sale_case_count}</h4>
                <h4>件あります</h4>
              </div>
              <Link href={'/console/company/sale_cases?status=rejected'}>
                <a className={styles.link_text}>差し戻し売却実績一覧</a>
              </Link>
            </div>
          )}

          {dashboardCount &&
            dashboardCount.rejected_company_profile_image_count > 0 &&
            !isMultipleCompanyUser &&
            !isMobile && (
              <div className={styles.info_wrap}>
                <div className={styles.company_info_title}>
                  <h4 className={styles.title_forward}>差し戻し紹介写真</h4>
                  <h4 className={styles.company_request_counter}>
                    {dashboardCount.rejected_company_profile_image_count}
                  </h4>
                  <h4>件あります</h4>
                </div>
                <Link href={`/console/company/company_detail/${companyId}/profile_images`}>
                  <a className={styles.link_text}>差し戻し紹介写真一覧</a>
                </Link>
              </div>
            )}

          {dashboardCount &&
            dashboardCount.rejected_company_profile_image_count > 0 &&
            isMultipleCompanyUser &&
            !isMobile && (
              <div className={styles.info_wrap}>
                <div className={styles.company_info_title}>
                  <h4 className={styles.title_forward}>差し戻し紹介写真</h4>
                  <h4 className={styles.company_request_counter}>
                    {dashboardCount.rejected_company_profile_image_count}
                  </h4>
                  <h4>件あります</h4>
                </div>
                <Link href={`/console/company/companies_list?rejected=true`}>
                  <a className={styles.link_text}>差し戻し紹介写真一覧</a>
                </Link>
              </div>
            )}

          {dashboardCount && dashboardCount.rejected_company_profile_count > 0 && !isMultipleCompanyUser && !isMobile && (
            <div className={styles.info_wrap}>
              <div className={styles.company_info_title}>
                <h4>差し戻し紹介文があります</h4>
              </div>
              <Link href={`/console/company/company_detail/${companyId}/profile`}>
                <a className={styles.link_text}>差し戻し紹介文はこちら</a>
              </Link>
            </div>
          )}

          {dashboardCount && dashboardCount.rejected_company_profile_count > 0 && isMultipleCompanyUser && !isMobile && (
            <div className={styles.info_wrap}>
              <div className={styles.company_info_title}>
                <h4>差し戻し紹介文があります</h4>
              </div>
              <Link href={`/console/company/companies_list?rejected=true`}>
                <a className={styles.link_text}>差し戻し紹介文一覧</a>
              </Link>
            </div>
          )}
          {dashboardCount && dashboardCount.rejected_disclosure_statement_count > 0 && !isMobile && (
            <div className={styles.info_wrap}>
              <div className={styles.company_info_title}>
                <h4 className={styles.title_forward}>差し戻し重説</h4>
                <h4 className={styles.company_request_counter}>{dashboardCount.rejected_disclosure_statement_count}</h4>
                <h4>件あります</h4>
              </div>
              <Link href={`/console/company/iqra_cloud/jusetsu/list?status=rejected`}>
                <a className={styles.link_text}>差し戻し重説一覧</a>
              </Link>
            </div>
          )}

          <Box sx={{ padding: isMobile ? '2rem' : '', backgroundColor: isMobile ? '#F9F9F9' : '' }}>
            <div className={styles.announcement_wrapper}>
              <h3 className={styles.section_title}>お知らせ</h3>
              {announcements && announcements.length > 0 ? (
                announcements.map((data, i) => (
                  <Link key={i} href={`/console/company/announcements/${data.id}`} passHref>
                    <div className={styles.announcement_row}>
                      <div className={styles.flex}>
                        <div className={styles.list_date}>
                          {data.publish_at && DateTime.fromISO(data.publish_at).toFormat(DateOutputFormatWithColon)}
                        </div>
                        <div className={styles.list_title}>{data.title}</div>
                      </div>
                      <img src="/images/iqfu/dashboard/arrow-right.svg" alt="arrow" />
                    </div>
                  </Link>
                ))
              ) : (
                <div>新着のお知らせはありません</div>
              )}
            </div>
          </Box>

          {isMobile ? (
            <Box mt={2} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 'bold' }}>売主へのご返信はこちら</Typography>
              <Link href="/console/company/chat/talk" passHref>
                <a className={styles.chat_talk_button}>売却相談を確認</a>
              </Link>
              {hasUnread && (
                <Box mt={1} sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <img
                    src="/images/iqfu/dashboard/company_dashboard_unread_icon.svg"
                    alt="未読"
                    className={styles.unread_icon}
                  />
                  <Typography sx={{ color: '#FFA506', fontSize: '1.2rem' }}>未確認のメッセージがあります</Typography>
                </Box>
              )}
              <Divider className={styles.button_divider} sx={{ my: '2rem' }} />
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 'bold' }}>運営へのお問合せはこちら</Typography>
              <Link href="/console/company/chat/contact" passHref>
                <div className={styles.chat_contact_button}>運営問合せ</div>
              </Link>
            </Box>
          ) : (
            <Box>
              <DashboardInfoCard />
              <DashboardSaleCases />
            </Box>
          )}
        </>
      )}
    </DashboardPageContainer>
  );
};

export default Dashboard;
